import { apiClient } from "../../../config/axios.config";
import type { PaginationResponse, Response } from "../../../types/api.types";
import type { createDeviceForm, DEVICE_CATEGORY_TYPE, DeviceStorageInput, getDeviceListForm, PARTNER_DEVICE_CATEGORY_TYPE, UpdateDeviceForm } from "../dtos/request/device.request";
import type { Device, DeviceDetail, DeviceOrderItem } from "../dtos/response/device.response";

const deviceModel = "/v1/device";
const adminDeviceModel = "/v1/admin/device";

function requireSuccess<T extends { error: boolean; message: string }>(data: T): T {
  if (!data || data.error !== false) {
    throw new Error(data?.message || "기종 API 응답을 확인할 수 없습니다.");
  }
  return data;
}

export const getAdminDeviceList = async (query: getDeviceListForm): Promise<PaginationResponse<Device>> => {
  const { data } = await apiClient.get(adminDeviceModel, { params: query });
  return requireSuccess(data);
};

export const getQuotationDeviceList = async (query: getDeviceListForm): Promise<PaginationResponse<Device>> => {
  const { data } = await apiClient.get(deviceModel, { params: query });
  return requireSuccess(data);
};

export const getDeviceDetail = async (id: string): Promise<Response<DeviceDetail>> => {
  const { data } = await apiClient.get(`${adminDeviceModel}/${id}`);
  return requireSuccess(data);
};

export const patchDevice = async (id: string, form: UpdateDeviceForm): Promise<Response<DeviceDetail>> => {
  const { data } = await apiClient.patch(`${adminDeviceModel}/${id}`, form);
  return requireSuccess(data);
};

export const createDevice = async (form: createDeviceForm): Promise<Response<DeviceDetail>> => {
  const { data } = await apiClient.post(adminDeviceModel, form);
  return requireSuccess(data);
};

export const deleteDevice = async (id: string | number): Promise<Response<string>> => {
  const { data } = await apiClient.delete(`${adminDeviceModel}/${id}`);
  return requireSuccess(data);
};

export const getDeviceOrderItems = async (excludeDeviceId: string | number): Promise<Response<DeviceOrderItem[]>> => {
  const { data } = await apiClient.get(`${adminDeviceModel}/order`, {
    params: { excludeDeviceId },
  });
  return requireSuccess(data);
};

export const patchDeviceOrder = async ({
  id,
  targetId,
  isFront,
}: {
  id: string | number;
  targetId: string | number;
  isFront: boolean;
}): Promise<Response<DeviceDetail>> => {
  const { data } = await apiClient.patch(`${adminDeviceModel}/${id}/order`, undefined, {
    params: { targetId, isFront },
  });
  return requireSuccess(data);
};

export const getDeviceQuotationList = async (type: DEVICE_CATEGORY_TYPE): Promise<PaginationResponse> => {
  const { data } = await apiClient.get(`${deviceModel}/quotation`, {
    params: { strategy: type },
  });
  return requireSuccess(data);
};

/**
 * 제휴용 기종 리스트 (GET /v1/device/fromPartner?category=GALAXY|IPHONE|KIDS)
 * TODO(backend): 응답은 유저앱 /byDailyQuote 와 동일 포맷으로 준다고 함 — 배열/페이지 어느 쪽이든 받도록 정규화
 */
export const getDeviceFromPartner = async (category: PARTNER_DEVICE_CATEGORY_TYPE): Promise<Device[]> => {
  const { data } = await apiClient.get<Response<Device[] | { content: Device[] }>>(`${deviceModel}/fromPartner`, {
    params: { category },
  });
  const result = requireSuccess(data).result;
  if (Array.isArray(result)) return result;
  return result?.content ?? [];
};

export const postDeviceColor = async (
  id: string | number,
  colorName: string,
  hexCode?: string,
  file?: File,
  imagePath?: string,
): Promise<Response<DeviceDetail>> => {
  const body = new FormData();
  if (file) body.append("multipartFile", file);

  const { data } = await apiClient.post(`${adminDeviceModel}/${id}/color`, file ? body : {}, {
    params: {
      colorName,
      ...(hexCode !== undefined ? { hexCode } : {}),
      ...(imagePath ? { imagePath } : {}),
    },
    ...(file ? { headers: { "Content-Type": "multipart/form-data" } } : {}),
  });
  return requireSuccess(data);
}

export const uploadDeviceColorImage = async (
  id: string | number,
  colorName: string,
  file: File,
  imagePath?: string,
): Promise<Response<DeviceDetail>> => {
  const formData = new FormData();
  formData.append("multipartFile", file);

  const { data } = await apiClient.post(`${adminDeviceModel}/${id}/color/image`, formData, {
    params: { colorName, ...(imagePath ? { imagePath } : {}) },
    headers: { "Content-Type": "multipart/form-data" },
  });
  return requireSuccess(data);
}

export const deleteDeviceColor = async (id: string | number, colorName: string): Promise<Response<DeviceDetail>> => {
  const { data } = await apiClient.delete(`${adminDeviceModel}/${id}/color`, {
    params: { colorName: colorName },
  });
  return requireSuccess(data);
}

export const patchDeviceColorActive = async (
  id: string | number,
  colorName: string,
  active: boolean,
): Promise<Response<DeviceDetail>> => {
  const { data } = await apiClient.patch(`${adminDeviceModel}/${id}/color/active`, undefined, {
    params: { colorName, active },
  });
  return requireSuccess(data);
};

export const upsertDeviceStorage = async (
  id: string | number,
  storage: DeviceStorageInput,
): Promise<Response<DeviceDetail>> => {
  const { data } = await apiClient.put(`${adminDeviceModel}/${id}/storage`, storage);
  return requireSuccess(data);
};

export const deleteDeviceStorage = async (
  id: string | number,
  storage: string,
): Promise<Response<DeviceDetail>> => {
  const { data } = await apiClient.delete(`${adminDeviceModel}/${id}/storage`, {
    params: { storage },
  });
  return requireSuccess(data);
};

export const patchDeviceStorageActive = async (
  id: string | number,
  storage: string,
  active: boolean,
): Promise<Response<DeviceDetail>> => {
  const { data } = await apiClient.patch(`${adminDeviceModel}/${id}/storage/active`, undefined, {
    params: { storage, active },
  });
  return requireSuccess(data);
};
